import { Injectable } from '@angular/core';
import { LocationAccuracy } from '@awesome-cordova-plugins/location-accuracy/ngx';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import {
  resolveCityFromGeocodeResults,
  resolveCountryFromGeocodeResults,
  resolveRegionFromGeocodeResults,
} from '../../../utils/geocode-city.util';
import { getCachedReverseGeocode } from '../../../utils/a-known-position-is-never-geocoded-twice';
import { SetLocationFilterEventsAction, UpdateRadiusExploreAction } from '../../events/state/filter-events.actions';
import { FilterEventsSelector } from '../../events/state/filter-events.selector';
import { SetUserLocationAction } from '../state/user-location.actions';
import { RemoveLocationAccessAction } from '../../app/state/user-location-access.action';
import { theServerMustForgetTheDevicePosition } from '../domain/a-withdrawn-permission-the-server-must-hear-about';
import { UpdateUserLiveLocationAction, UpdateUserLocationVisibilityAction } from '../state/user-profile.actions';
import { UserProfileSelector } from '../state/user-profile.selector';
import {
  clearProfileHomeLocationSnapshot,
  persistProfileHomeLocationSnapshot,
  readProfileHomeLocationSnapshot,
} from '../utils/profile-home-location.storage';
import { GeoLocality } from '../../locations';
import { LocationHistoryService } from '../../../services/location-history.service';
import { DEFAULT_EXPLORE_RADIUS_KM } from '../../events/domains/the-explore-radius-widens-only-when-nobody-chose-it';

type StoredLocation = {
  locality?: {
    city?: string;
    country?: string;
    countryCode?: string;
    formattedAddress?: string;
    region?: string;
    placeId?: string;
    hierarchy?: GeoLocality['hierarchy'];
    adminLevel?: GeoLocality['adminLevel'];
    suggestionDetail?: string;
    suggestionLabel?: string;
  };
  position?: {
    lat: number;
    lng: number;
  };
};

type ManualExploreSnapshot = StoredLocation & {
  radiusKm?: number;
  /**
   * Whether that radius was the person's or the default. Absent from a
   * snapshot saved before 15/10/2026, which reads as "not chosen": the radius
   * must still equal the default for the search to widen it.
   */
  radiusChosen?: boolean;
};

export type EnableLocationFailureReason = 'permission_denied' | 'location_services_disabled' | 'unknown';

export type OsLocationBlockReason = 'none' | 'permission_denied' | 'location_services_disabled';

type LocationPermissionState = 'granted' | 'denied' | 'location_services_disabled' | 'unknown';

@Injectable({
  providedIn: 'root',
})
export class LocationAccessService {
  private readonly locationAskedKey = 'locationAsked';
  private readonly locationEnabledKey = 'locationEnabled';
  private readonly locationEverEnabledKey = 'locationEverEnabled';
  private readonly exploreLocationManualKey = 'exploreLocationManuallySelected';
  private readonly exploreLocationManualSnapshotKey = 'exploreLocationManualSnapshot';
  private readonly pendingOsLocationServicesKey = 'pendingOsLocationServices';
  // 100 until 15/10/2026; see the-explore-radius-widens-only-when-nobody-chose-it.ts.
  readonly defaultExploreRadiusKm = DEFAULT_EXPLORE_RADIUS_KM;
  private readonly settingsChanged$ = new Subject<void>();
  private settingsNotifyCoalesceTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly store: Store,
    private readonly locationAccuracy: LocationAccuracy,
    private readonly locationHistory: LocationHistoryService,
  ) {}

  readonly locationSettingsChanged$: Observable<void> = this.settingsChanged$.asObservable();

  private locationEnableInProgress = false;
  private lastEnableFailureReason?: EnableLocationFailureReason;
  private osUnavailableNoticePending = false;
  private lastOsLocationBlockReason: OsLocationBlockReason = 'none';
  private wasOsLocationAvailable = true;

  getLastEnableFailureReason(): EnableLocationFailureReason | undefined {
    return this.lastEnableFailureReason;
  }

  getLastOsLocationBlockReason(): OsLocationBlockReason {
    return this.lastOsLocationBlockReason;
  }

  /**
   * True once after OS location became unavailable while the in-app
   * location feature was ON (device Location off or app permission Never).
   */
  consumeOsUnavailableNoticePending(): boolean {
    if (!this.osUnavailableNoticePending) {
      return false;
    }
    this.osUnavailableNoticePending = false;
    return true;
  }

  peekOsUnavailableNoticePending(): boolean {
    return this.osUnavailableNoticePending;
  }

  /**
   * Public entry used on resume when OS is still blocked — keeps preference ON
   * and queues the "Turn on device location" alert.
   */
  notifyOsLocationBlocked(reason: OsLocationBlockReason): void {
    this.markOsLocationUnavailable(reason);
  }

  /**
   * In-app preference ON and OS location currently usable.
   * Used by Explore map / nearby (toggle may stay ON while OS is blocked).
   */
  canUseDeviceLocationFeatures(): boolean {
    return this.isLocationEnabled() && this.lastOsLocationBlockReason === 'none';
  }

  /**
   * Reference point for event distance km on My Activities:
   * GPS when location is ON, profile home when OFF (never manual search).
   */
  async resolveEventReferenceLocation(): Promise<{ lat: number; lon: number } | undefined> {
    if (this.canUseDeviceLocationFeatures()) {
      try {
        const currentPosition = await this.readDevicePosition();
        return {
          lat: currentPosition.coords.latitude,
          lon: currentPosition.coords.longitude,
        };
      } catch (error) {
        console.warn('Failed to resolve GPS reference for events', error);
      }
    }
    const profileLocation = this.getProfileHomeStoredLocation();
    const position = this.resolveStoredPosition(profileLocation?.position);
    if (!position) {
      return undefined;
    }
    return { lat: position.lat, lon: position.lng };
  }

  async openSettingsForOsLocationBlock(reason: OsLocationBlockReason = this.lastOsLocationBlockReason): Promise<void> {
    // Two destinations for "Open settings":
    // 1) Device Location Services OFF → system Location (general)
    // 2) App permission Never / denied → App Info / app permissions
    if (reason === 'location_services_disabled') {
      await this.openDeviceLocationSettings();
      return;
    }

    await this.openAppLocationPermissionSettings();
  }

  /**
   * Fresh resolve then open the correct settings screen.
   * Used by Location & visibility "Open settings" (no confirm popup).
   */
  async openSettingsForCurrentOsLocationBlock(): Promise<void> {
    const reason = await this.resolveLocationVisibilityWarningReason();
    await this.openSettingsForOsLocationBlock(reason);
  }

  /**
   * Alert: "Turn on device location" / allow access — used when OS location
   * is lost while the user already had the feature enabled (not first ask).
   */
  async presentOsLocationUnavailableAlert(
    alertController: AlertController,
    translate: TranslateService,
  ): Promise<'open_settings' | 'cancel'> {
    const reason = this.lastOsLocationBlockReason;
    const isServicesOff = reason === 'location_services_disabled';
    const alert = await alertController.create({
      header: translate.instant(
        isServicesOff
          ? 'LOCATION_PERMISSION_MODAL.LOCATION_SERVICES_TITLE'
          : 'LOCATION_PERMISSION_MODAL.PERMISSION_DENIED_TITLE',
      ),
      message: translate.instant('LOCATION_PERMISSION_MODAL.DEVICE_ACCESS_REQUIRED_MESSAGE'),
      backdropDismiss: true,
      buttons: [
        {
          text: translate.instant('LOCATION_PERMISSION_MODAL.LATER'),
          role: 'cancel',
        },
        {
          text: translate.instant('LOCATION_PERMISSION_MODAL.OPEN_SETTINGS'),
          cssClass: 'open-settings',
        },
      ],
    });
    await alert.present();
    const { role } = await alert.onDidDismiss();
    const shouldOpenSettings = role !== 'cancel' && role !== 'backdrop';

    if (shouldOpenSettings) {
      await this.openSettingsForOsLocationBlock(reason);
      return 'open_settings';
    }

    return 'cancel';
  }

  async presentEnableLocationFailureGuidance(
    _alertController?: AlertController,
    _translate?: TranslateService,
  ): Promise<void> {
    if (!this.shouldOpenSettingsForLocationFailure()) {
      return;
    }

    await this.openAppLocationPermissionSettings();
  }

  shouldOpenSettingsForLocationFailure(): boolean {
    return !Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android';
  }

  isPendingEnableAfterOsLocationServices(): boolean {
    try {
      return sessionStorage.getItem(this.pendingOsLocationServicesKey) === 'true';
    } catch {
      return false;
    }
  }

  async openDeviceLocationSettings(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    this.setPendingEnableAfterOsLocationServices(true);

    const { AndroidSettings, IOSSettings, NativeSettings } = await import('capacitor-native-settings');

    if (Capacitor.getPlatform() === 'android') {
      // Open the device Location settings page (where GPS toggle lives),
      // not App Details — so the user can enable location services directly.
      await NativeSettings.openAndroid({
        option: AndroidSettings.Location,
      });
      return;
    }

    await NativeSettings.openIOS({
      option: IOSSettings.LocationServices,
    });
  }

  async openAppLocationPermissionSettings(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    // Do NOT set pending OS Location Services — App Info is for app
    // permission (Never). Pending flag is only for device Location OFF.
    const { AndroidSettings, IOSSettings, NativeSettings } = await import('capacitor-native-settings');

    if (Capacitor.getPlatform() === 'android') {
      await NativeSettings.openAndroid({
        option: AndroidSettings.ApplicationDetails,
      });
      return;
    }

    await NativeSettings.openIOS({
      option: IOSSettings.App,
    });
  }

  async resumePendingLocationEnable(): Promise<boolean> {
    if (!this.isPendingEnableAfterOsLocationServices()) {
      return false;
    }

    return this.enableLocationAccess();
  }

  async refreshGpsLocationWhenEnabled(): Promise<boolean> {
    if (!this.isLocationEnabled()) {
      return false;
    }

    const blockReason = await this.getOsLocationBlockReason();

    if (blockReason !== 'none') {
      this.markOsLocationUnavailable(blockReason);
      return false;
    }

    const recoveringFromOsBlock = !this.wasOsLocationAvailable;
    this.markOsLocationAvailable();
    this.clearPendingEnableAfterOsLocationServices();

    // Already healthy: do not re-apply GPS/filters on every resume — that
    // retriggers Explore loading and can leave the page stuck refreshing.
    if (!recoveringFromOsBlock) {
      return true;
    }

    const applied = await this.applyEnabledGpsLocation();

    if (applied) {
      this.notifySettingsChanged();
      return true;
    }

    const probeReason = await this.probeDeviceLocationServices();

    if (probeReason !== 'none') {
      this.markOsLocationUnavailable(probeReason);
    }

    return false;
  }

  isLocationEnableInProgress(): boolean {
    return this.locationEnableInProgress;
  }

  isLocationEnabled(): boolean {
    return localStorage.getItem(this.locationEnabledKey) === 'true';
  }

  isLocationAsked(): boolean {
    return localStorage.getItem(this.locationAskedKey) === 'true';
  }

  hasEverEnabledLocation(): boolean {
    return localStorage.getItem(this.locationEverEnabledKey) === 'true';
  }

  /**
   * Forgets, on this device, everything this service remembers about where
   * somebody is.
   *
   * ASYNC SINCE 15 OCTOBER 2026, and that is the whole point of the change.
   * Seven localStorage keys and the home-location snapshot were cleared here,
   * and `location_search_history` was not: up to five cities somebody had
   * typed into the city picker, with their coordinates, outlived sign-out and
   * account deletion on that phone. It was missed because it is the only one
   * of the lot that does not live in localStorage, so a reader checking this
   * method against localStorage found nothing missing.
   *
   * It never leaves the phone and holds no GPS reading, so it was not the
   * privacy policy's « position history »; it is still somebody's places, left
   * behind on a device they may be handing to someone else.
   */
  async clearPersistedStateOnLogout(): Promise<void> {
    localStorage.setItem(this.locationEnabledKey, 'false');
    localStorage.removeItem(this.locationAskedKey);
    localStorage.removeItem(this.locationEverEnabledKey);
    localStorage.removeItem(this.exploreLocationManualKey);
    localStorage.removeItem(this.exploreLocationManualSnapshotKey);
    localStorage.removeItem('filterEvents.location');
    localStorage.removeItem('filterEvents.radius');
    clearProfileHomeLocationSnapshot();
    // Ionic Storage, so awaited rather than fired and forgotten: a sign-out
    // that returns before the removal lands would leave the list in place on a
    // reload, which is the bug this method just had.
    await this.locationHistory.clearHistory();
  }

  markLocationAsked() {
    localStorage.setItem(this.locationAskedKey, 'true');
  }

  setLocationEnabled(enabled: boolean, options?: { silent?: boolean }) {
    localStorage.setItem(this.locationEnabledKey, `${enabled}`);
    if (enabled) {
      localStorage.setItem(this.locationEverEnabledKey, 'true');
      this.clearManualExploreLocationSelection();
      this.resetExploreRadiusToDefault();
      this.markLocationAsked();
      if (!options?.silent) {
        this.notifySettingsChanged();
      }
      return;
    }
    this.clearManualExploreLocationSelection();
    void this.revertLocationOnToggleOff(options?.silent);
    this.markLocationAsked();
  }

  private async revertLocationOnToggleOff(silent?: boolean): Promise<void> {
    await this.revertExploreStateOnLocationToggleOffAsync();
    void firstValueFrom(this.store.dispatch(new UpdateUserLocationVisibilityAction(false))).catch((error) => {
      console.warn('Failed to persist location visibility off', error);
    });
    if (!silent) {
      this.notifySettingsChanged();
    }
  }

  shouldUseGpsExploreDefaults(): boolean {
    if (!this.canUseDeviceLocationFeatures() || this.hasManuallySelectedExploreLocation()) {
      return false;
    }
    return true;
  }

  hasProfileExploreLocation(): boolean {
    return !!this.getProfileStoredLocation();
  }

  clearManualExploreLocationSelection(): void {
    localStorage.removeItem(this.exploreLocationManualKey);
    localStorage.removeItem(this.exploreLocationManualSnapshotKey);
  }

  /**
   * Clears a manual Explore city search and reloads the filter from device GPS.
   */
  async resetExploreSearchToCurrentLocation(): Promise<boolean> {
    if (!this.isLocationEnabled()) {
      return false;
    }

    return this.applyEnabledGpsLocation();
  }

  /**
   * Once per browser / WebView session: if in-app location is ON, drop
   * persisted "search another city" so Explore can reload from GPS.
   */
  resetExploreSearchToGpsDefaultsOnColdStart(): void {
    try {
      if (sessionStorage.getItem('smatchyExploreGpsSessionReset') === '1') {
        return;
      }
      sessionStorage.setItem('smatchyExploreGpsSessionReset', '1');
    } catch {
      return;
    }

    if (!this.canUseDeviceLocationFeatures()) {
      return;
    }

    this.clearManualExploreLocationSelection();
  }

  hasManuallySelectedExploreLocation(): boolean {
    return localStorage.getItem(this.exploreLocationManualKey) === 'true';
  }

  markExploreLocationManuallySelected(location: StoredLocation, radiusKm: number, radiusChosen = false): void {
    const snapshot: ManualExploreSnapshot = {
      ...location,
      radiusKm,
      radiusChosen,
    };

    localStorage.setItem(this.exploreLocationManualKey, 'true');
    localStorage.setItem(this.exploreLocationManualSnapshotKey, JSON.stringify(snapshot));
  }

  applyExploreStateWhenLocationDisabled(): void {
    void this.syncExploreStateWhenLocationDisabled();
  }

  async syncExploreStateWhenLocationDisabled(): Promise<void> {
    // A city chosen by hand is honoured whether or not the device location was
    // turned down.
    //
    // This used to require !hasExplicitlyDisabledLocation(), so anyone who had
    // declined the location prompt fell through to the profile city instead.
    // The explore page runs this on every ionViewWillEnter, so their chosen
    // city was overwritten on every return to the screen: pick Annecy, come
    // back, and the header says Paris again. Measured on 13 September 2026 as
    // two searches four seconds apart, the second one for a city nobody asked
    // for.
    //
    // The condition also had it backwards. Refusing GPS does not make a
    // hand-picked city less deliberate, it makes it the only way that person
    // has to choose one at all.
    const manualSnapshot = this.hasManuallySelectedExploreLocation()
      ? this.getManualExploreLocationSnapshot()
      : undefined;

    if (manualSnapshot) {
      await this.applyExploreLocationToFilter(manualSnapshot);
      this.applyExploreRadius(
        manualSnapshot.radiusKm ?? this.defaultExploreRadiusKm,
        true,
        manualSnapshot.radiusChosen === true,
      );
      return;
    }

    await this.syncExploreFilterToProfile();
    this.resetExploreRadiusToDefault(true);
  }

  resetExploreRadiusToDefault(skipSearch = false): void {
    this.applyExploreRadius(this.defaultExploreRadiusKm, skipSearch);
  }

  resolveLocationWhenToggleOff(): StoredLocation | undefined {
    // The hand-picked city wins here too.
    //
    // resolveExploreContextLocation checks the manual choice on its normal
    // path, but when device location is unavailable it returns this method
    // without consulting it at all. So the choice was honoured by the filter
    // and then thrown away by the page that reads it: the second of the two
    // searches measured on 13 September, four seconds after the first, for the
    // profile city nobody had asked for.
    const manualSnapshot = this.hasManuallySelectedExploreLocation()
      ? this.getManualExploreLocationSnapshot()
      : undefined;

    if (manualSnapshot) {
      return manualSnapshot;
    }

    return this.getProfileHomeStoredLocation() ?? this.getValidStoredExploreLocation();
  }

  revertExploreStateOnLocationToggleOff(): void {
    void this.revertExploreStateOnLocationToggleOffAsync();
  }

  private async revertExploreStateOnLocationToggleOffAsync(): Promise<void> {
    await this.syncExploreFilterToProfile();
    await this.restoreUserLocationFromProfile();
    this.resetExploreRadiusToDefault(true);
  }

  private applyExploreRadius(radiusKm: number, skipSearch = false, chosenByPerson = false): void {
    const currentRadiusMeter = this.store.selectSnapshot(FilterEventsSelector.radius);
    const targetRadiusMeter = radiusKm * 1000;
    const currentlyChosen = this.store.selectSnapshot(FilterEventsSelector.radiusChosen);

    // The mark counts as much as the value: the same 10 km, once chosen and
    // once the default, widens in one case and not the other.
    if (currentRadiusMeter === targetRadiusMeter && currentlyChosen === chosenByPerson) {
      return;
    }

    this.store.dispatch(new UpdateRadiusExploreAction(radiusKm, skipSearch, chosenByPerson));
  }

  private async syncExploreFilterToProfile(): Promise<void> {
    const profileLocation = this.getProfileHomeStoredLocation();

    if (!profileLocation) {
      return;
    }

    await this.applyExploreLocationToFilter(profileLocation);
  }

  private async restoreUserLocationFromProfile(): Promise<void> {
    const profileLocation = this.getProfileHomeStoredLocation();
    const position = this.resolveStoredPosition(profileLocation?.position);

    if (!position || !profileLocation?.locality) {
      return;
    }

    await firstValueFrom(
      this.store.dispatch(
        new SetUserLocationAction({
          locality: profileLocation.locality,
          position,
        }),
      ),
    );
  }

  private resolveStoredPosition(position?: {
    lat?: number;
    lng?: number;
    lon?: number;
  }): { lat: number; lng: number } | undefined {
    if (position?.lat === undefined || position?.lat === null) {
      return undefined;
    }

    const lng = position.lng ?? position.lon;

    if (lng === undefined || lng === null) {
      return undefined;
    }

    return { lat: position.lat, lng };
  }

  private async applyExploreLocationToFilter(location: StoredLocation): Promise<void> {
    const position = this.resolveStoredPosition(location.position);

    if (!position || !location.locality) {
      return;
    }

    const currentFilter = this.store.selectSnapshot(FilterEventsSelector.location);
    const currentPosition = this.resolveStoredPosition(currentFilter?.position);

    if (
      currentPosition?.lat === position.lat &&
      currentPosition?.lng === position.lng &&
      currentFilter?.locality?.city === location.locality.city &&
      currentFilter?.locality?.country === location.locality.country
    ) {
      return;
    }

    await firstValueFrom(
      this.store.dispatch(
        new SetLocationFilterEventsAction({
          locality: location.locality,
          position,
        }),
      ),
    );
  }

  private getManualExploreLocationSnapshot(): ManualExploreSnapshot | undefined {
    const rawSnapshot = localStorage.getItem(this.exploreLocationManualSnapshotKey);

    if (!rawSnapshot) {
      return undefined;
    }

    try {
      const snapshot = JSON.parse(rawSnapshot) as ManualExploreSnapshot;

      if (!snapshot?.position?.lat || !snapshot?.position?.lng || !snapshot?.locality) {
        return undefined;
      }

      return snapshot;
    } catch {
      return undefined;
    }
  }

  private notifySettingsChanged(): void {
    // Coalesce bursts (resume + enable + GPS apply) into one Explore reload.
    if (this.settingsNotifyCoalesceTimer) {
      return;
    }
    this.settingsNotifyCoalesceTimer = setTimeout(() => {
      this.settingsNotifyCoalesceTimer = undefined;
      this.settingsChanged$.next();
    }, 150);
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }

  private async readDevicePosition() {
    const isIos = Capacitor.getPlatform() === 'ios';
    const maxAttempts = isIos ? 3 : 1;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      try {
        return await Geolocation.getCurrentPosition({
          enableHighAccuracy: attempt === maxAttempts - 1,
          maximumAge: attempt === 0 ? 60000 : 0,
          timeout: isIos ? 15000 : 10000,
        });
      } catch (error) {
        if (attempt === maxAttempts - 1) {
          throw error;
        }

        await this.delay(400 * (attempt + 1));
      }
    }

    throw new Error('Failed to read device position');
  }

  shouldPromptLocationAccessOnLaunch(): boolean {
    if (this.locationEnableInProgress) {
      return false;
    }
    // User explicitly turned location off in Profile — do not re-prompt
    // or auto-enable on next launch/resume.
    if (this.hasExplicitlyDisabledLocation()) {
      return false;
    }

    return !this.isLocationEnabled() && !this.isLocationAsked();
  }

  /**
   * True when the user (or logout) persisted an intentional in-app OFF.
   * OS permission can still be granted — that must not re-enable the feature.
   */
  hasExplicitlyDisabledLocation(): boolean {
    return localStorage.getItem(this.locationEnabledKey) === 'false';
  }

  async hasOsLocationPermission(): Promise<boolean> {
    return this.hasGrantedLocationPermission();
  }

  /**
   * Resolves why device location cannot be used right now.
   * Covers both: system Location Services OFF, and app permission Never.
   * @param probeIfGranted When true, also probe GPS if permission is still
   *   granted — needed on resume because some devices keep reporting granted
   *   briefly after system Location is turned off.
   */
  async getOsLocationBlockReason(options?: { probeIfGranted?: boolean }): Promise<OsLocationBlockReason> {
    if (!Capacitor.isNativePlatform()) {
      this.lastOsLocationBlockReason = 'none';
      return 'none';
    }

    try {
      const permissions = await Geolocation.checkPermissions();
      // Prefer precise `location` over coarse — Never often denies fine
      // while coarse can still look granted on some Android builds.
      const primaryRaw = permissions.location ?? permissions.coarseLocation;
      const primary = primaryRaw ? String(primaryRaw).toLowerCase() : '';
      const coarse = permissions.coarseLocation ? String(permissions.coarseLocation).toLowerCase() : '';

      if (primary === 'denied' || primary === 'prompt-with-rationale') {
        this.lastOsLocationBlockReason = 'permission_denied';
        return 'permission_denied';
      }

      if (!primary && (coarse === 'denied' || coarse === 'prompt-with-rationale')) {
        this.lastOsLocationBlockReason = 'permission_denied';
        return 'permission_denied';
      }

      const isGranted = primary === 'granted' || coarse === 'granted';
      const isPromptOnly = primary === 'prompt' || (!primary && coarse === 'prompt');

      if (isGranted) {
        // iOS/Android can still report "granted" briefly (or cached)
        // after the user sets Never in App Info — probe to confirm.
        if (options?.probeIfGranted) {
          const probeReason = await this.probeDeviceLocationServices();
          this.lastOsLocationBlockReason = probeReason;
          return probeReason;
        }

        this.lastOsLocationBlockReason = 'none';
        return 'none';
      }

      // First-ask prompt only — not a hard block yet.
      if (isPromptOnly || (!primary && !coarse)) {
        this.lastOsLocationBlockReason = 'none';
        return 'none';
      }

      this.lastOsLocationBlockReason = 'permission_denied';
      return 'permission_denied';
    } catch (error) {
      // Capacitor rejects checkPermissions when system Location is OFF.
      if (this.isLocationServicesDisabledError(error)) {
        this.lastOsLocationBlockReason = 'location_services_disabled';
        return 'location_services_disabled';
      }

      // Permission APIs sometimes throw on Never instead of returning
      // denied — distinguish from device Location Services OFF.
      if (this.isPermissionDeniedError(error)) {
        this.lastOsLocationBlockReason = 'permission_denied';
        return 'permission_denied';
      }

      // Any other checkPermissions rejection → device location unavailable.
      this.lastOsLocationBlockReason = 'location_services_disabled';
      return 'location_services_disabled';
    }
  }

  /**
   * Warning reason for Location & visibility screen.
   * Preference stays ON; when OS/app blocks GPS → red text + Open settings.
   * Uses GPS probe so iOS "Never" is detected even if checkPermissions
   * still reports granted (common until the next cold start).
   */
  async resolveLocationVisibilityWarningReason(): Promise<OsLocationBlockReason> {
    this.ensureInAppPreferenceOnWhileOsBlocked();

    if (!this.isLocationEnabled()) {
      return 'none';
    }

    const previousReason = this.lastOsLocationBlockReason;
    const wasUnavailable = !this.wasOsLocationAvailable;
    const reason = await this.getOsLocationBlockReason({
      probeIfGranted: true,
    });

    if (reason !== 'none') {
      return reason;
    }

    // Explore may already know location is unusable (map hidden) while a
    // flaky probe briefly reports OK after App Info → Never.
    if (wasUnavailable && previousReason !== 'none') {
      this.lastOsLocationBlockReason = previousReason;
      this.wasOsLocationAvailable = false;
      return previousReason;
    }

    // Secondary check via permission state (covers denied without probe).
    const permissionState = await this.getLocationPermissionState();
    if (permissionState === 'denied' || permissionState === 'unknown') {
      this.lastOsLocationBlockReason = 'permission_denied';
      return 'permission_denied';
    }
    if (permissionState === 'location_services_disabled') {
      this.lastOsLocationBlockReason = 'location_services_disabled';
      return 'location_services_disabled';
    }

    return 'none';
  }

  /**
   * Keep in-app toggle ON when OS/app permission blocks location and the
   * user had already enabled the feature (Figma EN-28).
   */
  ensureInAppPreferenceOnWhileOsBlocked(): void {
    if (!this.hasEverEnabledLocation()) {
      return;
    }

    if (this.lastOsLocationBlockReason === 'none') {
      return;
    }

    localStorage.setItem(this.locationEnabledKey, 'true');
  }

  /**
   * Explore detected location is unusable while preference is ON.
   * Syncs OS block reason so Location & visibility shows red text +
   * Open settings (App Info for Never / permission denied).
   * Fast path only — no GPS probe (avoids white-screen flash on resume).
   */
  async syncOsBlockWhenDeviceLocationUnusable(): Promise<OsLocationBlockReason> {
    if (!this.isLocationEnabled() && !this.hasEverEnabledLocation()) {
      return this.lastOsLocationBlockReason;
    }

    if (this.hasEverEnabledLocation()) {
      localStorage.setItem(this.locationEnabledKey, 'true');
    }

    // Caller already knows location is unusable — do not probe GPS.
    const reason = await this.getOsLocationBlockReason({
      probeIfGranted: false,
    });

    if (reason !== 'none') {
      this.markOsLocationUnavailable(reason);
      return reason;
    }

    // checkPermissions still "granted" but Explore cannot use location
    // (typical after setting Never in App Info) → App Info CTA.
    this.markOsLocationUnavailable('permission_denied');
    return 'permission_denied';
  }

  async isOsLocationAvailable(): Promise<boolean> {
    return (await this.getOsLocationBlockReason()) === 'none';
  }

  async reconcileLocationState(): Promise<boolean> {
    if (this.locationEnableInProgress) {
      return this.isLocationEnabled();
    }

    if (this.isLocationEnabled()) {
      // Probe when "granted" so Never (App Info) is not missed while
      // Capacitor still caches a granted permission state.
      const blockReason = await this.getOsLocationBlockReason({
        probeIfGranted: true,
      });

      if (blockReason !== 'none') {
        if (this.hasEverEnabledLocation()) {
          localStorage.setItem(this.locationEnabledKey, 'true');
        }
        this.markOsLocationUnavailable(blockReason);
        return false;
      }

      const recoveringFromOsBlock = !this.wasOsLocationAvailable;
      this.markOsLocationAvailable();

      if (recoveringFromOsBlock) {
        this.notifySettingsChanged();
      } else {
        await this.syncLocationVisibilityWithBackend(true);
      }

      return true;
    }

    // Respect explicit in-app OFF across restarts (even if OS permission remains).
    if (this.hasExplicitlyDisabledLocation()) {
      const blockReason = await this.getOsLocationBlockReason();
      this.lastOsLocationBlockReason = blockReason;
      return false;
    }

    const permissionState = await this.getLocationPermissionState();

    if (permissionState !== 'granted' || !this.hasStoredLocation()) {
      return false;
    }

    this.setLocationEnabled(true);
    return true;
  }

  /**
   * OS location blocked while in-app preference stays ON (Figma EN-28).
   * Map/nearby hide via canUseDeviceLocationFeatures(); settings shows red text.
   */
  private markOsLocationUnavailable(reason: OsLocationBlockReason): void {
    const alreadyBlocked = !this.wasOsLocationAvailable && this.lastOsLocationBlockReason === reason;

    this.wasOsLocationAvailable = false;
    this.lastOsLocationBlockReason = reason;
    this.lastEnableFailureReason =
      reason === 'location_services_disabled' ? 'location_services_disabled' : 'permission_denied';
    this.osUnavailableNoticePending = true;
    this.applyExploreStateWhenLocationDisabled();

    // Avoid Explore reload loops when already in the same OS-blocked state.
    if (!alreadyBlocked) {
      this.notifySettingsChanged();
      this.tellTheServerToForgetThePosition(reason);
    }
  }

  /**
   * Asks the API to erase the stored device position when somebody has taken
   * the location permission away from Smatchy.
   *
   * ON THE TRANSITION ONLY, which is what `alreadyBlocked` above buys: this
   * runs once when the block is first seen, not on every reconcile. A launch
   * while still blocked must not send the same call again.
   *
   * THE IN-APP PREFERENCE DELIBERATELY STAYS ON, as Figma EN-28 asks, so the
   * settings screen can still explain that it is the phone blocking rather
   * than a setting somebody turned off. The server and the phone therefore
   * disagree on `locationEnabled` while the block lasts, and that is wanted:
   * when the permission comes back, syncLocationVisibilityWithBackend stops
   * early-returning and writes the position again.
   *
   * FIRE AND FORGET. The state handler already swallows its own failure, and a
   * server that cannot be reached must not keep the application from hiding
   * the map, which is the part that matters to the person in front of it.
   */
  private tellTheServerToForgetThePosition(reason: OsLocationBlockReason): void {
    const somebodyIsSignedIn = !!this.store.selectSnapshot(UserProfileSelector.profile);

    if (!theServerMustForgetTheDevicePosition(reason, somebodyIsSignedIn)) {
      return;
    }

    void firstValueFrom(this.store.dispatch(new RemoveLocationAccessAction())).catch((error) => {
      console.warn('Failed to tell the server the location permission is gone', error);
    });
  }

  private markOsLocationAvailable(): void {
    this.wasOsLocationAvailable = true;
    this.lastOsLocationBlockReason = 'none';
  }

  private async probeDeviceLocationServices(): Promise<OsLocationBlockReason> {
    try {
      // Keep this short — a long getCurrentPosition hang after App Info
      // → Never freezes the WebView and looks like a white flash.
      await Geolocation.getCurrentPosition({
        enableHighAccuracy: false,
        maximumAge: 0,
        timeout: 800,
      });
      return 'none';
    } catch (error) {
      if (this.isLocationServicesDisabledError(error)) {
        return 'location_services_disabled';
      }
      if (this.isPermissionDeniedError(error)) {
        return 'permission_denied';
      }

      // Never / App Info revoke often updates only after a failed read.
      try {
        const permissions = await Geolocation.checkPermissions();
        const primary = String(permissions.location ?? permissions.coarseLocation ?? '').toLowerCase();

        if (primary === 'denied' || primary === 'prompt-with-rationale') {
          return 'permission_denied';
        }
      } catch (recheckError) {
        if (this.isLocationServicesDisabledError(recheckError)) {
          return 'location_services_disabled';
        }
        if (this.isPermissionDeniedError(recheckError)) {
          return 'permission_denied';
        }
      }

      // Transient GPS/timeout while permission still looks granted.
      return 'none';
    }
  }

  private isLocationServicesDisabledError(error: unknown): boolean {
    const message = this.getErrorMessage(error).toLowerCase();
    return (
      message.includes('location services are not enabled') ||
      message.includes('location service is turned off') ||
      message.includes('location provider is disabled') ||
      message.includes('location disabled') ||
      message.includes('gps is disabled') ||
      message.includes('network location is disabled') ||
      message.includes('settings.secure')
    );
  }

  /**
   * Why a device position could not be read, or 'none' when the error says
   * nothing about permissions. Public because a screen that asks for a
   * position has to answer the member rather than let the rejection reach
   * Angular's global handler, and the rules live here.
   */
  blockReasonForError(error: unknown): OsLocationBlockReason {
    if (this.isLocationServicesDisabledError(error)) {
      return 'location_services_disabled';
    }
    if (this.isPermissionDeniedError(error)) {
      return 'permission_denied';
    }
    return 'none';
  }

  /**
   * Core Location's own error, which iOS hands over in the DEVICE's language.
   *
   * The message checks below are written in English, so « L'opération n'a pas
   * pu s'achever. (kCLErrorDomain erreur 1.) » matched none of them, and a
   * French iPhone that refused location came out as « not a permission
   * problem ». The two parts iOS never translates are the domain name and the
   * number, so those are what is read here. Code 1 is kCLErrorDenied; 0 is
   * « location unknown » and 2 a network failure, and neither is a refusal.
   *
   * Seen in production on 20 September 2026, CAPACITOR-M, iOS 18.7.
   */
  private isCoreLocationDenial(error: unknown): boolean {
    const found = /kclerrordomain[^0-9]*(\d+)/.exec(this.getErrorMessage(error).toLowerCase());
    return found?.[1] === '1';
  }

  private isPermissionDeniedError(error: unknown): boolean {
    if (this.isLocationServicesDisabledError(error)) {
      return false;
    }

    if (this.isCoreLocationDenial(error)) {
      return true;
    }

    if (error && typeof error === 'object') {
      const code = (error as { code?: unknown }).code;
      // Geolocation PositionError.PERMISSION_DENIED === 1
      if (code === 1 || code === '1') {
        return true;
      }
    }

    const message = this.getErrorMessage(error).toLowerCase();
    return (
      message.includes('not authorized') ||
      message.includes('permission denied') ||
      message.includes('permissions denied') ||
      message.includes('access denied') ||
      message.includes('authorization') ||
      message.includes('denied')
    );
  }

  private getErrorMessage(error: unknown): string {
    if (!error) {
      return '';
    }
    if (typeof error === 'string') {
      return error;
    }
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'object' && 'message' in error) {
      return String((error as { message?: unknown }).message ?? '');
    }
    return String(error);
  }

  private async syncLocationVisibilityWithBackend(enabled: boolean): Promise<void> {
    if (!enabled) {
      return;
    }

    const profile = this.store.selectSnapshot(UserProfileSelector.profile);

    if (profile?.locationEnabled === true && profile?.locationCity) {
      return;
    }

    try {
      const hasPermission = await this.hasGrantedLocationPermission();

      if (!hasPermission) {
        return;
      }

      const currentPosition = await this.readDevicePosition();
      const lat = currentPosition.coords.latitude;
      const lng = currentPosition.coords.longitude;
      const locality = await this.resolveLocalityFromCoordinates(lat, lng);

      await firstValueFrom(
        this.store.dispatch([
          new SetUserLocationAction({
            locality,
            position: { lat, lng },
          }),
          new UpdateUserLiveLocationAction(),
        ]),
      );
    } catch (error) {
      console.warn('Failed to sync location visibility with backend', error);
    }
  }

  hasStoredLocation(): boolean {
    const storedExploreLocation = this.getStoredExploreLocation();
    const hasExploreLocation =
      !!storedExploreLocation?.position?.lat &&
      !!storedExploreLocation?.position?.lng &&
      !!storedExploreLocation?.locality?.formattedAddress;

    if (hasExploreLocation) {
      return true;
    }

    const profileLocation = this.store.selectSnapshot(UserProfileSelector.location);

    return !!profileLocation?.lat && !!profileLocation?.lng;
  }

  getStoredLocationLabel(): string | undefined {
    const storedExploreLocation = this.getStoredExploreLocation();

    if (storedExploreLocation?.locality?.formattedAddress) {
      return storedExploreLocation.locality.formattedAddress;
    }

    if (storedExploreLocation?.locality?.city) {
      return [storedExploreLocation.locality.city, storedExploreLocation.locality.country].filter(Boolean).join(', ');
    }

    const profile = this.store.selectSnapshot(UserProfileSelector.profile);

    if (!profile) {
      return undefined;
    }

    return [profile.city, profile.country].filter(Boolean).join(', ');
  }

  async enableLocationAccess(): Promise<boolean> {
    this.locationEnableInProgress = true;
    this.lastEnableFailureReason = undefined;

    try {
      const hasPermission = await this.requestLocationPermission();

      if (!hasPermission) {
        if (this.lastEnableFailureReason !== 'location_services_disabled') {
          if (this.isPendingEnableAfterOsLocationServices()) {
            this.lastEnableFailureReason = 'location_services_disabled';
          } else {
            this.lastEnableFailureReason = 'permission_denied';
            this.clearPendingEnableAfterOsLocationServices();
          }
        }

        this.handleEnableFailureWhileKeepingPreferenceIfNeeded();
        return false;
      }

      this.preserveProfileHomeSnapshotBeforeGps();
      const applied = await this.applyEnabledGpsLocation();

      if (!applied) {
        const userHandledLocationServices = await this.requestAndroidLocationServicesInApp();

        if (!userHandledLocationServices) {
          this.lastEnableFailureReason = 'location_services_disabled';
          this.clearPendingEnableAfterOsLocationServices();
          this.handleEnableFailureWhileKeepingPreferenceIfNeeded();
          return false;
        }

        const appliedAfterPrompt = await this.applyEnabledGpsLocation();

        if (!appliedAfterPrompt) {
          this.lastEnableFailureReason = 'location_services_disabled';
          this.clearPendingEnableAfterOsLocationServices();
          this.handleEnableFailureWhileKeepingPreferenceIfNeeded();
          return false;
        }
      }

      this.clearPendingEnableAfterOsLocationServices();
      this.markOsLocationAvailable();
      this.setLocationEnabled(true, { silent: true });

      try {
        await firstValueFrom(this.store.dispatch(new UpdateUserLiveLocationAction()));
      } catch (error) {
        console.warn('Failed to persist user default location', error);
      }

      return true;
    } catch (error) {
      console.warn('Failed to enable location access', error);
      this.lastEnableFailureReason = 'unknown';
      this.clearPendingEnableAfterOsLocationServices();
      this.setLocationEnabled(false, { silent: true });

      return false;
    } finally {
      this.locationEnableInProgress = false;
      this.notifySettingsChanged();
    }
  }

  private handleEnableFailureWhileKeepingPreferenceIfNeeded(): void {
    const reason = this.lastEnableFailureReason;
    const isOsBlock = reason === 'location_services_disabled' || reason === 'permission_denied';

    if (isOsBlock && this.hasEverEnabledLocation()) {
      localStorage.setItem(this.locationEnabledKey, 'true');
      this.markOsLocationUnavailable(
        reason === 'location_services_disabled' ? 'location_services_disabled' : 'permission_denied',
      );
      return;
    }

    this.setLocationEnabled(false, { silent: true });
  }

  private async applyEnabledGpsLocation(): Promise<boolean> {
    try {
      const currentPosition = await this.readDevicePosition();
      const lat = currentPosition.coords.latitude;
      const lng = currentPosition.coords.longitude;
      const locality = await this.resolveLocalityFromCoordinates(lat, lng);
      const position = { lat, lng };

      this.clearManualExploreLocationSelection();

      await firstValueFrom(
        this.store.dispatch([
          new SetUserLocationAction({
            locality,
            position,
          }),
          new SetLocationFilterEventsAction({
            locality,
            position,
          }),
          new UpdateRadiusExploreAction(this.defaultExploreRadiusKm, true),
        ]),
      );

      return true;
    } catch (error) {
      console.warn('Failed to read device GPS position', error);
      return false;
    }
  }

  private setPendingEnableAfterOsLocationServices(pending: boolean): void {
    try {
      if (pending) {
        sessionStorage.setItem(this.pendingOsLocationServicesKey, 'true');
      } else {
        sessionStorage.removeItem(this.pendingOsLocationServicesKey);
      }
    } catch {
      // ignore storage errors
    }
  }

  private clearPendingEnableAfterOsLocationServices(): void {
    this.setPendingEnableAfterOsLocationServices(false);
  }

  private getStoredExploreLocation(): StoredLocation | undefined {
    // The filter keeps its location as null once cleared, which is the same
    // nothing this method has always answered.
    return this.store.selectSnapshot(FilterEventsSelector.location) ?? undefined;
  }

  private getValidStoredExploreLocation(): StoredLocation | undefined {
    const storedLocation = this.getStoredExploreLocation();

    if (!storedLocation?.position?.lat || !storedLocation?.position?.lng || !storedLocation?.locality) {
      return undefined;
    }

    return storedLocation;
  }

  private preserveProfileHomeSnapshotBeforeGps(): void {
    if (readProfileHomeLocationSnapshot()) {
      return;
    }

    const homeLocation = this.getProfileStoredLocation();
    const position = this.resolveStoredPosition(homeLocation?.position);

    if (!position || !homeLocation?.locality?.city) {
      return;
    }

    persistProfileHomeLocationSnapshot({
      locality: homeLocation.locality,
      position,
    });
  }

  private getProfileHomeStoredLocation(): StoredLocation | undefined {
    const homeSnapshot = readProfileHomeLocationSnapshot();

    if (homeSnapshot?.locality && homeSnapshot.position) {
      return {
        locality: homeSnapshot.locality,
        position: homeSnapshot.position,
      };
    }

    return this.getProfileStoredLocation();
  }

  private getProfileStoredLocation(): StoredLocation | undefined {
    const profile = this.store.selectSnapshot(UserProfileSelector.profile);
    const profilePosition = this.store.selectSnapshot(UserProfileSelector.location);
    const profileLocality = this.store.selectSnapshot(UserProfileSelector.locality);

    if (!profile?.city || !profilePosition?.lat || !profilePosition?.lng) {
      return undefined;
    }

    return {
      locality: {
        city: profileLocality?.city || profile.city,
        country: profileLocality?.country || profile.country || '',
        countryCode: profileLocality?.countryCode,
        formattedAddress:
          profileLocality?.formattedAddress || [profile.city, profile.country].filter(Boolean).join(', '),
        region: profileLocality?.region || '',
        placeId: profileLocality?.placeId,
        hierarchy: profileLocality?.hierarchy,
        adminLevel: profileLocality?.adminLevel,
        suggestionDetail: profileLocality?.suggestionDetail,
        suggestionLabel: profileLocality?.suggestionLabel,
      },
      position: profilePosition,
    };
  }

  private async requestAndroidLocationServicesInApp(): Promise<boolean> {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
      return false;
    }

    try {
      await this.locationAccuracy.request(this.locationAccuracy.REQUEST_PRIORITY_HIGH_ACCURACY);

      return true;
    } catch (error) {
      console.warn('Android location services dialog failed', error);
      return false;
    }
  }

  private async hasGrantedLocationPermission(): Promise<boolean> {
    return (await this.getLocationPermissionState()) === 'granted';
  }

  private async getLocationPermissionState(): Promise<LocationPermissionState> {
    try {
      const permissions = await Geolocation.checkPermissions();
      const locationPermission = permissions.location ?? permissions.coarseLocation;

      if (locationPermission === 'granted') {
        return 'granted';
      }

      // Explicit app-level denial (e.g. Never allow) — do not confuse with
      // device Location Services being off via LocationAccuracy.canRequest().
      if (locationPermission === 'denied') {
        return 'denied';
      }

      if (Capacitor.getPlatform() === 'android') {
        return this.getAndroidLocationAuthorizationState();
      }

      return 'denied';
    } catch (error) {
      if (this.isLocationServicesDisabledError(error)) {
        return 'location_services_disabled';
      }

      if (Capacitor.getPlatform() === 'android') {
        return this.getAndroidLocationAuthorizationState();
      }

      return 'unknown';
    }
  }

  private async getAndroidLocationAuthorizationState(): Promise<LocationPermissionState> {
    try {
      const canRequestLocationServices = await this.locationAccuracy.canRequest();

      return canRequestLocationServices ? 'location_services_disabled' : 'denied';
    } catch {
      return 'location_services_disabled';
    }
  }

  private async requestLocationPermission(): Promise<boolean> {
    if (Capacitor.getPlatform() === 'android') {
      return this.requestAndroidPermissionThenLocationServices();
    }

    let permissions: Awaited<ReturnType<typeof Geolocation.checkPermissions>>;

    try {
      permissions = await Geolocation.checkPermissions();
    } catch (error) {
      return false;
    }

    const currentPermission = permissions.location ?? permissions.coarseLocation;

    if (currentPermission === 'granted') {
      return true;
    }

    const requestedPermissions = await Geolocation.requestPermissions();
    const requestedPermission = requestedPermissions.location ?? requestedPermissions.coarseLocation;

    if (requestedPermission === 'granted') {
      return true;
    }

    if (this.isLikelyDeviceLocationServicesDisabled(currentPermission)) {
      this.lastEnableFailureReason = 'location_services_disabled';
      this.setPendingEnableAfterOsLocationServices(true);
      // Ask Android to enable device Location Services without leaving Smatchy.
      const userHandledLocationServices = await this.requestAndroidLocationServicesInApp();

      if (userHandledLocationServices) {
        const permissionsAfterLocationServices = await Geolocation.requestPermissions();
        const permissionAfterLocationServices =
          permissionsAfterLocationServices.location ?? permissionsAfterLocationServices.coarseLocation;

        if (permissionAfterLocationServices === 'granted') {
          return true;
        }
      }

      if (Capacitor.getPlatform() !== 'android') {
        await this.openDeviceLocationSettings();
      }
    }

    return false;
  }

  private async requestAndroidPermissionThenLocationServices(): Promise<boolean> {
    const locationServicesEnabled = await this.requestAndroidLocationServicesInApp();

    if (!locationServicesEnabled) {
      this.lastEnableFailureReason = 'location_services_disabled';
      this.clearPendingEnableAfterOsLocationServices();
      return false;
    }

    try {
      let permissions = await Geolocation.checkPermissions();
      let permission = permissions.location ?? permissions.coarseLocation;

      if (permission !== 'granted') {
        permissions = await Geolocation.requestPermissions();
        permission = permissions.location ?? permissions.coarseLocation;
      }

      if (permission !== 'granted') {
        this.lastEnableFailureReason = 'permission_denied';
        this.clearPendingEnableAfterOsLocationServices();
        return false;
      }
    } catch (error) {
      console.warn('Android location permission request failed', error);
      this.lastEnableFailureReason = 'permission_denied';
      this.clearPendingEnableAfterOsLocationServices();
      return false;
    }

    return true;
  }

  private isLikelyDeviceLocationServicesDisabled(permissionBeforeRequest: string | undefined): boolean {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    if (this.isPendingEnableAfterOsLocationServices()) {
      return true;
    }

    // When device location/GPS is off, Android often skips the app permission
    // sheet and returns "denied" immediately — this can happen from both
    // "prompt" state (first ask) and "denied" state (already asked before).
    return permissionBeforeRequest === 'prompt' || permissionBeforeRequest === 'denied';
  }

  private async resolveLocalityFromCoordinates(lat: number, lng: number) {
    const profile = this.store.selectSnapshot(UserProfileSelector.profile);

    try {
      // From this phone's memory when the member has not moved: see
      // a-known-position-is-never-geocoded-twice.
      const results = await getCachedReverseGeocode({ lat, lng });
      const city = resolveCityFromGeocodeResults(results);
      const country = resolveCountryFromGeocodeResults(results) ?? profile?.country ?? '';
      const region = resolveRegionFromGeocodeResults(results) ?? '';
      const formattedAddress = results[0]?.formatted_address ?? [city, country].filter(Boolean).join(', ');

      return {
        city: city ?? `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
        country,
        formattedAddress,
        region,
      };
    } catch (error) {
      console.warn('Failed to reverse geocode current position', error);

      return {
        city: `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
        country: profile?.country ?? '',
        formattedAddress: `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
        region: '',
      };
    }
  }
}
